/**
 * The day timeline — `/countries/:cc/field/timeline?user_id&date` (v17 F4).
 * THE deliverable: one engineer's country-local day read forward — clocked in
 * → arrived → left → clocked out — with the stops on a plot beside it.
 *
 * ===========================================================================
 * ONE FETCH, THE COMPOSED DAY — THE SERVER ALREADY RULED EVERY HARD QUESTION
 * ===========================================================================
 * `GET /countries/:cc/field/timeline` composes attendance + visits + session
 * spans through each stream's own Rule-14 choke point. This page adds NO
 * interpretation: the model (`timeline-model.ts`) orders what arrived by the
 * server's instants, the plot draws only RAW positions, and every restriction
 * arrives as data (`'•••'` per stamp, `sessions_visible:false` for the span
 * list) and is rendered as a stated fact. The page never asks `can()` about
 * coordinates — the payload IS the answer, row by row, and self-viewing
 * engineers (raw without the key) are exactly why a client-side permission
 * guess would be wrong.
 *
 * ===========================================================================
 * A VIEWER WITHOUT THE COORDINATES KEY GETS THE DERIVED FACTS, NOT A BROKEN MAP
 * ===========================================================================
 * The unit brief's own sentence. When every stamp is `'•••'` the plot panel
 * is replaced by a designed statement — the times, the order, the arrival
 * agreement and the distances (visit-level facts, F0 D4) are all still on the
 * page, because they are the caller's to see; the coordinates are not, and an
 * empty axes-and-nothing SVG would read as a defect rather than a boundary.
 *
 * THE DAY IS THE SERVER'S. `date` is the country-local day and the response
 * echoes it; prev/next just move the URL parameter. Event TIMES render in the
 * viewer's own clock (the house `formatTime`) with the exact instant in the
 * title — the register idiom, stated here once.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { formatDateLong, formatTime } from '../inquiries/format';
import { cn } from '../../lib/cn';
import { fetchTimeline, fieldErrorText, fieldKeys } from './api';
import type { FieldTimeline, LiveSessionRow, VisitRow } from './api';
import {
  classifyAgreement,
  classifyPosition,
  formatAccuracy,
  formatArrivalDistance,
  formatCoordinates,
} from './position';
import { composeDayEvents, extractStops, projectStops, scaleBar } from './timeline-model';
import type { StopExtraction, TimelineEvent } from './timeline-model';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** ±1 day on a YYYY-MM-DD, in UTC arithmetic (a calendar step, no tz involved). */
function shiftDay(date: string, days: number): string {
  const base = new Date(`${date}T12:00:00Z`);
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
}

export function TimelinePage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const location = useLocation();

  // Mirrors the API's AND (`@RequirePerm('field.attendance.view', 'field.visits.view')`):
  // the composition demands what each register demands.
  const canView = can(countryCode, 'field.attendance.view') && can(countryCode, 'field.visits.view');

  const userId = params.get('user_id');
  const date = params.get('date');
  const wellFormed =
    userId !== null && /^\d+$/.test(userId) && date !== null && DATE_PATTERN.test(date);

  /** Name carried by the register link's router state — a hint, superseded by data. */
  const stateName =
    typeof (location.state as { engineerName?: unknown } | null)?.engineerName === 'string'
      ? ((location.state as { engineerName: string }).engineerName)
      : null;

  const day = useQuery({
    queryKey: fieldKeys.timeline(countryCode, userId ?? '', date ?? ''),
    queryFn: ({ signal }) => fetchTimeline(countryCode, userId ?? '', date ?? '', signal),
    enabled: canView && wellFormed,
  });

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="The day timeline is not available to this account">
          The timeline composes the attendance register and the visit register, so it needs
          both field view permissions in {countryCode}. Ask an administrator to widen your
          access.
        </Alert>
      </div>
    );
  }

  if (!wellFormed) {
    // The route needs an engineer and a day; the registers are where both come from.
    return (
      <div className="flex flex-col gap-4 p-6">
        <PageHeader title="Day timeline" />
        <div className="rounded-md border border-dashed border-line bg-surface">
          <EmptyState
            title="Pick an engineer and a day first."
            description="Open the day timeline from a row of the attendance or visit register — each row carries the engineer and the day it is about."
            action={
              <span className="flex gap-4">
                <Link
                  to={`/countries/${countryCode}/field/attendance`}
                  className="text-body13 font-medium text-indigo hover:underline"
                >
                  Attendance register
                </Link>
                <Link
                  to={`/countries/${countryCode}/field/visits`}
                  className="text-body13 font-medium text-indigo hover:underline"
                >
                  Visit register
                </Link>
              </span>
            }
          />
        </div>
      </div>
    );
  }

  const setDate = (next: string): void => {
    const patch = new URLSearchParams(params);
    patch.set('date', next);
    setParams(patch, { replace: true });
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      {day.isPending ? (
        <>
          <PageHeader title="Day timeline" subtitle={formatDateLong(`${date}T12:00:00Z`)} />
          <div className="rounded-md border border-line bg-surface px-4">
            <SkeletonRows rows={6} label="Loading the day timeline" />
          </div>
        </>
      ) : day.isError ? (
        <>
          <PageHeader title="Day timeline" subtitle={formatDateLong(`${date}T12:00:00Z`)} />
          <Alert tone="danger" title="The day timeline could not be loaded">
            <p>{fieldErrorText(day.error)}</p>
            <button
              type="button"
              onClick={() => {
                void day.refetch();
              }}
              className="mt-1 font-medium underline underline-offset-2"
            >
              Try again.
            </button>
          </Alert>
        </>
      ) : (
        <TimelineBody day={day.data} fallbackName={stateName} onDate={setDate} />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── body ── */

function engineerNameOf(day: FieldTimeline, fallback: string | null): string {
  return (
    day.attendance?.user_name ??
    day.visits.find((visit) => visit.user_name !== null)?.user_name ??
    day.sessions.find((session) => session.engineer_name !== null)?.engineer_name ??
    fallback ??
    `Engineer #${day.user_id}`
  );
}

function TimelineBody({
  day,
  fallbackName,
  onDate,
}: {
  day: FieldTimeline;
  fallbackName: string | null;
  onDate: (next: string) => void;
}): React.JSX.Element {
  const events = useMemo(() => composeDayEvents(day), [day]);
  const stops = useMemo(() => extractStops(events), [events]);
  const name = engineerNameOf(day, fallbackName);

  const nothingRecorded =
    day.attendance === null && day.visits.length === 0 && day.sessions.length === 0;

  return (
    <>
      <PageHeader
        title={name}
        subtitle={`${formatDateLong(`${day.date}T12:00:00Z`)} · ${day.country}`}
        actions={<DayStepper date={day.date} onDate={onDate} />}
      />

      {nothingRecorded ? (
        <div className="rounded-md border border-dashed border-line bg-surface">
          <EmptyState
            title="No field activity was recorded on this day."
            description={
              day.sessions_visible
                ? 'No clock-in, no visits and no live sessions. If the engineer worked, nothing was captured — the timeline does not infer a day that was not recorded.'
                : 'No clock-in and no visits. Live-session records are restricted for your account, so this page cannot say whether any ran.'
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_460px]">
          <div className="flex flex-col gap-4">
            <EventList events={events} attendanceMissing={day.attendance === null} />
            {day.attendance?.missing_clock_out === true ? (
              <Alert tone="warning" title="Not clocked out">
                The day ended with the cycle still open. The server never invents a closing
                time; the gap is the record.
              </Alert>
            ) : null}
            <SessionsPanel visible={day.sessions_visible} sessions={day.sessions} />
          </div>
          <StopsPanel stops={stops} />
        </div>
      )}
    </>
  );
}

/* ──────────────────────────────────────────────────────────── day stepper ── */

function DayStepper({
  date,
  onDate,
}: {
  date: string;
  onDate: (next: string) => void;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        aria-label="Previous day"
        onClick={() => onDate(shiftDay(date, -1))}
        className="inline-flex size-8 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:bg-subtle"
      >
        <ChevronLeft aria-hidden="true" className="size-4" />
      </button>
      <input
        type="date"
        aria-label="Day"
        value={date}
        onChange={(event) => {
          if (DATE_PATTERN.test(event.target.value)) onDate(event.target.value);
        }}
        className="h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg [color-scheme:light] dark:[color-scheme:dark]"
      />
      <button
        type="button"
        aria-label="Next day"
        onClick={() => onDate(shiftDay(date, 1))}
        className="inline-flex size-8 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:bg-subtle"
      >
        <ChevronRight aria-hidden="true" className="size-4" />
      </button>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── event list ── */

const EVENT_LABEL: Record<TimelineEvent['kind'], string> = {
  'clock-in': 'Clocked in',
  arrive: 'Arrived',
  leave: 'Left',
  'clock-out': 'Clocked out',
};

function EventList({
  events,
  attendanceMissing,
}: {
  events: readonly TimelineEvent[];
  attendanceMissing: boolean;
}): React.JSX.Element {
  /*
   * The plot numbers RAW stops 1..n; the list shows the same number beside the
   * events that ARE those stops, so the two surfaces cross-reference. Events
   * whose stamp is restricted or absent carry no number — there is no dot to
   * point at. Precomputed BEFORE the JSX (not `seq += 1` inside the map
   * callback): the react-compiler lint refuses a render-phase reassignment,
   * and it is right to — a memoized re-render would double-count.
   */
  const stopNumbers: (number | null)[] = [];
  for (const event of events) {
    if (classifyPosition(event.position).kind === 'raw') {
      stopNumbers.push(stopNumbers.filter((value) => value !== null).length + 1);
    } else {
      stopNumbers.push(null);
    }
  }
  return (
    <ol className="flex flex-col rounded-md border border-line bg-surface" aria-label="Day events">
      {attendanceMissing ? (
        <li className="border-b border-line px-4 py-3 text-body13 text-fg-secondary">
          No clock-in was recorded on this day.
        </li>
      ) : null}
      {events.map((event, index) => {
        const stopNo = stopNumbers[index] ?? null;
        return (
          <li
            key={`${event.kind}-${event.at}-${String(index)}`}
            className="flex items-start gap-3 border-b border-line px-4 py-3 last:border-b-0"
          >
            <span
              aria-hidden="true"
              className={cn(
                'mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-pill text-label font-medium',
                stopNo === null
                  ? 'bg-subtle text-fg-muted ring-1 ring-line'
                  : 'bg-indigo-soft text-indigo-strong',
              )}
            >
              {stopNo ?? '·'}
            </span>

            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <p className="text-body13 text-fg">
                <span className="font-medium">{EVENT_LABEL[event.kind]}</span>
                {event.visit !== undefined ? (
                  <span>
                    {' '}
                    — {event.visit.party_name ?? `Party #${event.visit.party_id}`}
                    <span className="capitalize text-fg-secondary"> · {event.visit.purpose}</span>
                  </span>
                ) : null}
              </p>

              {event.kind === 'arrive' && event.visit !== undefined ? (
                <ArrivalFacts visit={event.visit} />
              ) : null}
              {event.kind === 'leave' &&
              event.visit !== undefined &&
              event.visit.outcome_notes !== null ? (
                <p className="text-body13 text-fg-secondary">{event.visit.outcome_notes}</p>
              ) : null}

              <PositionLine event={event} />
            </div>

            <span className="tnum shrink-0 text-body13 text-fg" title={event.at}>
              {formatTime(event.at)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The arrive event's derived facts — visible whatever the coordinates' state
 * (F0 D4). The TONE rides a pill on its own tinted ground (4.5:1, mode-
 * invariant); the sentence stays `text-fg-secondary`, because tone TEXT on the
 * dark surface measures 3.2x:1 — under AA — and `field.contrast.test.ts` pins
 * that refusal.
 */
function ArrivalFacts({ visit }: { visit: VisitRow }): React.JSX.Element | null {
  // v17 F-1: the metres may be '•••' for this seat; the BAND is what carries
  // the signal then. One call, so this component never branches on the mask.
  const distance = formatArrivalDistance(visit.arrival_distance_m, visit.arrival_distance_band);
  const agreement = classifyAgreement(visit.arrival_agreement);
  /*
   * ⚠ v17 RULING 6 — THE RESTRICTED ARM EXISTS BECAUSE THE FALLBACK LIED.
   * This component used to test 'agrees', then 'disagrees', then fall through
   * to "No position comparison — one side had no coordinates at arrival." The
   * moment the server began masking the agreement, that fall-through became the
   * copy a Coordinator saw on a 40 km visit — and every word of it was false:
   * a comparison WAS made, both sides HAD coordinates, and the reader was being
   * told the opposite while a band reading "over 5 km" sat two lines away. The
   * sentence had lost its subject. It gets its own, below, and it keeps the
   * band — which is the finding the owner refused to give up in F-1.
   */
  if (agreement.kind === 'restricted') {
    return (
      <p className="flex flex-wrap items-center gap-2 text-body13 text-fg-secondary">
        <span
          data-agreement="restricted"
          className="inline-flex items-center whitespace-nowrap rounded-pill bg-subtle px-2.5 py-1 text-label font-medium text-fg ring-1 ring-line"
        >
          Comparison restricted
        </span>
        <span>
          Whether this position matched the customer&apos;s saved location is restricted for your
          account
          {distance === '' ? '' : ` — the recorded distance band is ${distance}`}.
        </span>
      </p>
    );
  }
  if (agreement.kind === 'agrees') {
    return (
      <p className="flex flex-wrap items-center gap-2 text-body13 text-fg-secondary">
        <span
          data-agreement="agrees"
          className="inline-flex items-center whitespace-nowrap rounded-pill bg-ok-bg px-2.5 py-1 text-label font-medium text-ok-fg"
        >
          Position agrees
        </span>
        <span>
          with the customer&apos;s saved location{distance === '' ? '' : ` (${distance})`}.
        </span>
      </p>
    );
  }
  if (agreement.kind === 'disagrees') {
    return (
      <p className="flex flex-wrap items-center gap-2 text-body13 text-fg-secondary">
        <span
          data-agreement="disagrees"
          className="inline-flex items-center whitespace-nowrap rounded-pill bg-warn-bg px-2.5 py-1 text-label font-medium text-warn-fg"
        >
          Position differs
        </span>
        <span>
          from the customer&apos;s saved location
          {distance === '' ? '' : ` — ${distance} away`}. Recorded, not judged.
        </span>
      </p>
    );
  }
  return (
    <p className="text-body13 text-fg-secondary">
      No position comparison — one side had no coordinates at arrival.
    </p>
  );
}

function PositionLine({ event }: { event: TimelineEvent }): React.JSX.Element {
  const state = classifyPosition(event.position);
  if (state.kind === 'absent') {
    return <p className="text-body13 text-fg-secondary">Position not captured.</p>;
  }
  if (state.kind === 'restricted') {
    return (
      <p className="text-body13 text-fg-secondary" title="•••">
        Position restricted for your account.
      </p>
    );
  }
  const accuracy = formatAccuracy(state.view);
  return (
    <p
      className="font-mono text-mono text-fg-secondary"
      title={`Device reported at ${state.view.captured_at} (server receipt ${state.view.recorded_at})`}
    >
      {formatCoordinates(state.view)}
      {accuracy === '' ? null : <span className="ml-1 text-fg-muted">{accuracy}</span>}
    </p>
  );
}

/* ───────────────────────────────────────────────────────────────── sessions ── */

function SessionsPanel({
  visible,
  sessions,
}: {
  visible: boolean;
  sessions: readonly LiveSessionRow[];
}): React.JSX.Element {
  return (
    <section
      aria-label="Live sessions"
      className="flex flex-col gap-2 rounded-md border border-line bg-surface px-4 py-3"
    >
      <h2 className="text-th uppercase text-fg-muted">Live sessions</h2>
      {!visible ? (
        /*
         * B4's list rule (self or `field.location.view`), stated — never a
         * silent empty. `sessions_visible:false` is the server SAYING it
         * withheld the records; rendering "none" here would be the page
         * asserting a fact it was refused.
         */
        <p data-sessions-state="restricted" className="text-body13 text-fg-secondary">
          Live-session records are restricted for your account. Viewing another
          engineer&apos;s sessions needs the location permission.
        </p>
      ) : sessions.length === 0 ? (
        <p data-sessions-state="none" className="text-body13 text-fg-secondary">
          No live sessions ran on this day.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {sessions.map((session) => (
            <li key={session.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="tnum text-body13 text-fg">
                {formatTime(session.started_at ?? session.created_at)}
                {session.ended_at !== null ? ` – ${formatTime(session.ended_at)}` : ''}
              </span>
              <span className="text-body13 text-fg-secondary">
                {session.requested_by_user_id === null
                  ? 'Started by the engineer'
                  : `Requested by ${session.requested_by_name ?? `#${session.requested_by_user_id}`}, accepted`}
                {' · '}
                {session.duration_minutes} min bound
              </span>
              <span
                className={cn(
                  'inline-flex items-center whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium',
                  session.tracking_active
                    ? 'bg-info-bg text-info-fg'
                    : 'bg-subtle text-fg ring-1 ring-line',
                )}
              >
                {session.tracking_active ? 'Tracking now' : (session.end_reason ?? session.status)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────── the plot ── */

const PLOT_WIDTH = 460;
const PLOT_HEIGHT = 340;
const PLOT_PAD = 36;

const STOP_KIND_LABEL: Record<TimelineEvent['kind'], string> = {
  'clock-in': 'Clock in',
  arrive: 'Arrive',
  leave: 'Leave',
  'clock-out': 'Clock out',
};

function StopsPanel({ stops }: { stops: StopExtraction }): React.JSX.Element {
  if (stops.stops.length === 0) {
    if (stops.restricted > 0) {
      /*
       * The derived-facts state, not a broken map: coordinates exist and the
       * server withheld every one of them from this caller (Rule 14). The
       * page's left column still carries times, order, agreement and
       * distances — the facts that are this seat's to read.
       */
      return (
        <section
          aria-label="Stops"
          data-plot-state="restricted"
          className="flex flex-col gap-2 rounded-md border border-line bg-surface px-4 py-3"
        >
          <h2 className="text-th uppercase text-fg-muted">Stops</h2>
          <p className="text-body13 text-fg-secondary">
            {String(stops.restricted)}{' '}
            {stops.restricted === 1 ? 'position stamp was' : 'position stamps were'} recorded on
            this day, but their coordinates are restricted for your account — the map needs the
            location permission. The timeline beside this still shows when each stop happened
            and whether arrivals agreed with customers&apos; saved locations.
          </p>
        </section>
      );
    }
    return (
      <section
        aria-label="Stops"
        data-plot-state="empty"
        className="flex flex-col gap-2 rounded-md border border-line bg-surface px-4 py-3"
      >
        <h2 className="text-th uppercase text-fg-muted">Stops</h2>
        <p className="text-body13 text-fg-secondary">
          No positions were captured on this day
          {stops.absent > 0 ? ' — the stamps exist but carry no coordinates (device GPS off, or purged under the retention window)' : ''}
          .
        </p>
      </section>
    );
  }

  const plot = projectStops(stops.stops, PLOT_WIDTH, PLOT_HEIGHT, PLOT_PAD);
  const bar = scaleBar(plot.metersPerUnit, PLOT_WIDTH / 3);

  return (
    <section
      aria-label="Stops"
      data-plot-state="plotted"
      className="flex flex-col gap-2 rounded-md border border-line bg-surface px-4 py-3"
    >
      <h2 className="text-th uppercase text-fg-muted">Stops</h2>
      <svg
        viewBox={`0 0 ${String(PLOT_WIDTH)} ${String(PLOT_HEIGHT)}`}
        role="img"
        aria-label={`${String(stops.stops.length)} stops, numbered in time order`}
        className="w-full max-w-full rounded-sm bg-subtle"
      >
        {/*
         * An SVG PLOT of the day's stops, deliberately not a street map: the
         * repo has no map dependency and none was added tonight (recorded
         * decision — `timeline-model.ts` header). Relative geometry, numbered
         * in time order, with a real scale bar: what a day review needs,
         * nothing it cannot verify.
         */}
        {plot.points.length > 1 ? (
          <polyline
            points={plot.points.map((point) => `${String(point.x)},${String(point.y)}`).join(' ')}
            fill="none"
            stroke="var(--crf-accent-indigo)"
            strokeWidth="1.5"
            strokeDasharray="4 3"
            opacity="0.7"
          />
        ) : null}
        {plot.points.map((point) => (
          <g key={point.seq}>
            <circle
              cx={point.x}
              cy={point.y}
              r="11"
              fill="var(--crf-bg-surface)"
              stroke="var(--crf-accent-indigo)"
              strokeWidth="1.5"
            />
            <text
              x={point.x}
              y={point.y}
              textAnchor="middle"
              dominantBaseline="central"
              className="tnum"
              fill="var(--crf-text-primary)"
              fontSize="11"
              fontWeight="600"
            >
              {point.seq}
            </text>
            <title>
              {`${String(point.seq)} · ${STOP_KIND_LABEL[point.kind]}${point.label === null ? '' : ` — ${point.label}`} · ${formatTime(point.at)}`}
            </title>
          </g>
        ))}
        {bar !== null ? (
          <g>
            <line
              x1={PLOT_PAD}
              y1={PLOT_HEIGHT - 14}
              x2={PLOT_PAD + bar.units}
              y2={PLOT_HEIGHT - 14}
              stroke="var(--crf-text-secondary)"
              strokeWidth="2"
            />
            <text
              x={PLOT_PAD + bar.units / 2}
              y={PLOT_HEIGHT - 20}
              textAnchor="middle"
              // text/primary, NOT secondary: secondary on the subtle canvas is
              // 4.40:1 in light — under AA for this 10px label (pinned).
              fill="var(--crf-text-primary)"
              fontSize="10"
            >
              {bar.label}
            </text>
          </g>
        ) : null}
      </svg>

      <ol className="flex flex-col gap-1" aria-label="Stop legend">
        {plot.points.map((point) => (
          <li key={point.seq} className="flex items-center gap-2 text-body13 text-fg-secondary">
            <span className="tnum inline-flex size-5 shrink-0 items-center justify-center rounded-pill bg-indigo-soft text-label font-medium text-indigo-strong">
              {point.seq}
            </span>
            <span className="min-w-0 truncate">
              {STOP_KIND_LABEL[point.kind]}
              {point.label === null ? '' : ` — ${point.label}`}
            </span>
            <span className="tnum ml-auto shrink-0" title={point.at}>
              {formatTime(point.at)}
            </span>
          </li>
        ))}
      </ol>

      {stops.restricted > 0 ? (
        <p className="text-body13 text-fg-secondary">
          {String(stops.restricted)} further {stops.restricted === 1 ? 'stamp is' : 'stamps are'}{' '}
          restricted for your account and not plotted.
        </p>
      ) : null}
      {stops.absent > 0 ? (
        <p className="text-body13 text-fg-secondary">
          {String(stops.absent)} {stops.absent === 1 ? 'event' : 'events'} carried no captured
          position.
        </p>
      ) : null}
    </section>
  );
}
